import { ApiConfig } from "./ConstantService"

// ─── error helper ─────────────────────────────────────────────────────────────

export class AccessError extends Error {
  constructor(message) {
    super(message);
    this.name = "AccessError";
  }
}

async function safeFetch(url, options) {
  let resp;
  try {
    resp = await fetch(url, options);
  } catch {
    // fetch threw before we could read the response — the browser blocks the
    // response when CORS headers are absent (can happen on any non-2xx from
    // API Gateway, not only auth errors). Show a generic message so the user
    // isn't misled into thinking it's an access issue.
    throw new Error(
      "Unable to reach the server. The server may be temporarily unavailable — please try again later."
    );
  }

  if (resp.status === 401) {
    throw new AccessError(
      "Your session has expired. Please refresh the page to log in again."
    );
  }

  const data = await resp.json();

  if (!resp.ok) {
    const msg = data?.message || `Request failed (${resp.status})`;
    // Backend user-validation errors carry a specific phrasing — treat as access errors.
    if (
      msg.toLowerCase().includes("not admin user") ||
      msg.toLowerCase().includes("not se team user") ||
      msg.toLowerCase().includes("not authorized") ||
      msg.toLowerCase().includes("unauthorized")
    ) {
      throw new AccessError(msg);
    }
    throw new Error(msg);
  }

  return data;
}

// ─── API functions ─────────────────────────────────────────────────────────────

const headers = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

export async function CheckAccess(token) {
  return safeFetch(ApiConfig.endpoint + "/me", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetDashboardAvailableDates(token) {
  return safeFetch(ApiConfig.endpoint + "/dashboard/available_dates", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetDashboard(token) {
  return safeFetch(ApiConfig.endpoint + "/dashboard", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function CreateDashboardJob(token, date_from, date_to) {
  return safeFetch(ApiConfig.endpoint + "/dashboard/jobs", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify({ date_from, date_to }),
  });
}

export async function GetDashboardJob(token, job_id) {
  return safeFetch(ApiConfig.endpoint + `/dashboard/jobs/${job_id}`, {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetSolutionMappings(token) {
  return safeFetch(ApiConfig.endpoint + "/solution_mapping", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function AddSolutionMapping(token, entry) {
  return safeFetch(ApiConfig.endpoint + "/solution_mapping", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify(entry),
  });
}

export async function UpdateSolutionMappings(token, mappings) {
  return safeFetch(ApiConfig.endpoint + "/solution_mapping", {
    method: "PUT",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify(mappings),
  });
}

export async function SubmitAccessRequest(token) {
  return safeFetch(ApiConfig.endpoint + "/access_request", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetAccessRequests(token) {
  return safeFetch(ApiConfig.endpoint + "/access_requests", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function ApproveAccessRequest(token, email) {
  return safeFetch(ApiConfig.endpoint + "/access_requests/approve", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify({ email }),
  });
}

export async function RejectAccessRequest(token, email) {
  return safeFetch(ApiConfig.endpoint + `/access_requests/${encodeURIComponent(email)}`, {
    method: "DELETE",
    mode: "cors",
    headers: headers(token),
  });
}

export async function RevokeUser(token, email) {
  return safeFetch(ApiConfig.endpoint + `/users/${encodeURIComponent(email)}`, {
    method: "DELETE",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetUnresolvedEntities(token) {
  return safeFetch(ApiConfig.endpoint + "/unresolved_entities", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetUsers(token, is_admin) {
  const url_path = is_admin ? "/admin_users" : "/users";
  return safeFetch(ApiConfig.endpoint + url_path, {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function SetUsers(token, users, is_admin) {
  const url_path = is_admin ? "/admin_users" : "/users";
  return safeFetch(ApiConfig.endpoint + url_path, {
    method: "PUT",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify(users),
  });
}

export async function TrackVisit(token) {
  return safeFetch(ApiConfig.endpoint + "/visits", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetVisits(token) {
  return safeFetch(ApiConfig.endpoint + "/visits", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetYtdReport(token) {
  return safeFetch(ApiConfig.endpoint + "/ytd_report", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function GetEntityTypes(token) {
  return safeFetch(ApiConfig.endpoint + "/backfill_entity/entity_types", {
    method: "GET",
    mode: "cors",
    headers: headers(token),
  });
}

export async function SubmitBackfillEntity(token, payload) {
  return safeFetch(ApiConfig.endpoint + "/backfill_entity", {
    method: "POST",
    mode: "cors",
    headers: headers(token),
    body: JSON.stringify(payload),
  });
}
